import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Paper, Tabs, Tab, Chip, CircularProgress, Button, Divider, Select, MenuItem, FormControl, InputLabel, Snackbar, Alert } from '@mui/material';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Tooltip as ChartTooltip, Legend, Filler } from 'chart.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

Chart.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTooltip, Legend, Filler);

const dashboardVersions = [
  { version: 'v1', label: 'Classic' },
  { version: 'v2', label: 'Modern' },
  { version: 'v3', label: 'Minimal' }
];

// Fallback dummy analytics data
const dummyAnalytics = {
  metrics: {
    impressions: 1000,
    engagements: 200,
    likes: 150,
    comments: 30,
    shares: 20,
    clicks: 50,
    followersGained: 10,
    saves: 5,
    reactions: 40,
    watchTime: 120,
    totalPosts: 5,
    engagementRate: 20,
    errorRate: 0
  },
  timeRanges: {
    daily: { impressions: 200, engagements: 40 },
    weekly: { impressions: 700, engagements: 140 },
    monthly: { impressions: 1000, engagements: 200 }
  },
  breakdowns: {
    platforms: {
      facebook: { engagements: 100, impressions: 500, followersGained: 5, likes: 80, comments: 10, shares: 5 },
      instagram: { engagements: 100, impressions: 500, followersGained: 5, likes: 70, comments: 20, shares: 15 }
    },
    handles: { 'user1': { totalPosts: 3 }, 'user2': { totalPosts: 2 } },
    mediaTypes: { image: 3, video: 2, text: 0 },
    postStatus: { posted: 5, scheduled: 0, failed: 0 }
  },
  trends: {
    engagement: { dates: ['2025-08-24','2025-08-25','2025-08-26','2025-08-27','2025-08-28'], values: [20,40,60,80,100] }
  },
  top: {
    hashtags: { marketing: 3, automation: 2 },
    links: { 'http://example.com': 2 }
  },
  posts: [
    { id: '1', caption: 'Dummy Post 1', date: '2025-08-24T10:00:00Z', platforms: ['facebook'], status: 'posted', metrics: { engagements: 20, impressions: 100, likes: 15, comments: 2, shares: 1, clicks: 5, followersGained: 1, saves: 0, reactions: 4, watchTime: 10 }, hashtags: ['marketing'], links: ['http://example.com'] },
    { id: '2', caption: 'Dummy Post 2', date: '2025-08-25T12:00:00Z', platforms: ['instagram'], status: 'posted', metrics: { engagements: 40, impressions: 200, likes: 30, comments: 5, shares: 2, clicks: 10, followersGained: 2, saves: 1, reactions: 8, watchTime: 20 }, hashtags: ['automation'], links: [] },
    { id: '3', caption: 'Dummy Post 3', date: '2025-08-26T14:00:00Z', platforms: ['facebook'], status: 'posted', metrics: { engagements: 60, impressions: 300, likes: 45, comments: 10, shares: 5, clicks: 15, followersGained: 3, saves: 2, reactions: 12, watchTime: 30 }, hashtags: ['marketing'], links: ['http://example.com'] },
    { id: '4', caption: 'Dummy Post 4', date: '2025-08-27T16:00:00Z', platforms: ['instagram'], status: 'posted', metrics: { engagements: 80, impressions: 400, likes: 60, comments: 13, shares: 7, clicks: 20, followersGained: 4, saves: 2, reactions: 16, watchTime: 40 }, hashtags: ['automation'], links: [] },
    { id: '5', caption: 'Dummy Post 5', date: '2025-08-28T18:00:00Z', platforms: ['facebook'], status: 'posted', metrics: { engagements: 100, impressions: 500, likes: 80, comments: 20, shares: 10, clicks: 25, followersGained: 5, saves: 3, reactions: 20, watchTime: 50 }, hashtags: ['marketing'], links: ['http://example.com'] }
  ]
};

const OutputDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [version, setVersion] = useState('v2');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success'|'error'|'info'}>({open: false, message: '', severity: 'info'});
  // Engagement Heatmap State
  const [heatmapData, setHeatmapData] = useState<number[][]>([]);
  const [heatmapLabels, setHeatmapLabels] = useState<string[]>([]);
  const [heatmapHours, setHeatmapHours] = useState<string[]>([]);
  // Follower Growth Trends
  const [followerTrendData, setFollowerTrendData] = useState<any>(null);
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
          const res = await axios.get(`${axios.defaults.baseURL}/api/analytics`);
        if (!res.data || !res.data.posts || res.data.posts.length === 0) {
          setAnalytics(dummyAnalytics);
        } else {
          setAnalytics(res.data);
        }
        setError('');
      } catch (e: any) {
        setAnalytics(dummyAnalytics);
        setError(e.message || 'Failed to fetch analytics');
      }
      setLoading(false);
    };
    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (Array.isArray(analytics?.posts)) {
      // Build heatmap: rows = days, cols = hours
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const hours = Array.from({length: 24}, (_, i) => `${i}:00`);
      const data = Array(7).fill(0).map(() => Array(24).fill(0));
      analytics.posts.forEach((post: any) => {
        if (!post || typeof post !== 'object' || !post.date || !post.metrics || typeof post.metrics.engagements !== 'number' || isNaN(post.metrics.engagements)) return;
        const date = new Date(post.date);
        if (isNaN(date.getTime())) return;
        const day = date.getDay(); // 0=Sun, 6=Sat
        const hour = date.getHours();
        // Engagements as metric
        const engagements = post.metrics.engagements;
        if (typeof day === 'number' && typeof hour === 'number' && day >= 0 && day <= 6 && hour >= 0 && hour <= 23) {
          data[(day + 6) % 7][hour] += engagements;
        }
      });
      setHeatmapData(data);
      setHeatmapLabels(days);
      setHeatmapHours(hours);
    }
  }, [analytics]);

  useEffect(() => {
    if (Array.isArray(analytics?.posts)) {
      // Aggregate follower growth by date
      const growthByDate: Record<string, number> = {};
      analytics.posts.forEach((post: any) => {
        if (!post || typeof post !== 'object' || !post.date || !post.metrics || typeof post.metrics.followersGained !== 'number' || isNaN(post.metrics.followersGained)) return;
        const dateStr = typeof post.date === 'string' ? post.date.split('T')[0] : '';
        if (!dateStr) return;
        const gained = post.metrics.followersGained;
        growthByDate[dateStr] = (growthByDate[dateStr] || 0) + gained;
      });
      const dates = Object.keys(growthByDate).sort();
      const values = dates.map(date => growthByDate[date]);
      setFollowerTrendData({ labels: dates, datasets: [{ label: 'Followers Gained', data: values, backgroundColor: 'rgba(49,130,206,0.2)', borderColor: '#3182ce', tension: 0.4 }] });
    }
  }, [analytics]);

  // Helper: Filter posts by platform
  const filteredPosts = Array.isArray(analytics?.posts)
    ? analytics.posts.filter((post: any) => filterPlatform === 'all' || (Array.isArray(post.platforms) && post.platforms.includes(filterPlatform)))
    : [];

  // Helper: Snackbar
  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  // Helper: Export CSV
  const handleExportCSV = () => {
    if (!analytics) return;
    const header = 'ID,Caption,Date,Platforms,Status,Impressions,Engagements,Likes,Comments,Shares,Clicks,FollowersGained,Saves,Reactions,WatchTime,Hashtags,Links';
    const rows = analytics.posts.map((post: any) =>
      `${post.id},"${post.caption}",${post.date},${post.platforms?.join('|')},${post.status},${post.metrics.impressions},${post.metrics.engagements},${post.metrics.likes},${post.metrics.comments},${post.metrics.shares},${post.metrics.clicks},${post.metrics.followersGained},${post.metrics.saves},${post.metrics.reactions},${post.metrics.watchTime},"${post.hashtags?.join('|')}","${post.links?.join('|')}"`
    );
    const csv = [header, ...rows].join('\n');
    navigator.clipboard.writeText(csv);
    setSnackbar({open: true, message: 'CSV copied to clipboard!', severity: 'success'});
  };

  // Export PDF logic
  const handleExportPDF = () => {
    if (!analytics) return;
    const doc = new jsPDF();
    doc.text('Social Media Analytics Report', 14, 16);
    const tableData = analytics.posts.map((post: any) => [
      post.id,
      post.caption,
      post.date,
      post.platforms?.join('|'),
      post.status,
      post.metrics.impressions,
      post.metrics.engagements,
      post.metrics.likes,
      post.metrics.comments,
      post.metrics.shares,
      post.metrics.clicks,
      post.metrics.followersGained,
      post.metrics.saves,
      post.metrics.reactions,
      post.metrics.watchTime,
      post.hashtags?.join('|'),
      post.links?.join('|')
    ]);
    autoTable(doc, {
      head: [['ID','Caption','Date','Platforms','Status','Impressions','Engagements','Likes','Comments','Shares','Clicks','FollowersGained','Saves','Reactions','WatchTime','Hashtags','Links']],
      body: tableData,
      startY: 24
    });
    doc.save('analytics_report.pdf');
    setSnackbar({open: true, message: 'PDF downloaded!', severity: 'success'});
  };

  // Top Performing Content logic
  const getTopPosts = (posts: any[], count = 5) => {
    return [...posts]
      .sort((a, b) => (b.metrics?.engagements || 0) - (a.metrics?.engagements || 0))
      .slice(0, count);
  };

  // Platform Comparison logic
  const getPlatformComparisonData = (breakdowns: any) => {
    const platforms = Object.keys(breakdowns.platforms);
    const metrics = ['engagements', 'impressions', 'followersGained', 'likes', 'comments', 'shares'];
    return {
      labels: platforms,
      datasets: metrics.map(metric => ({
        label: metric,
        data: platforms.map(p => breakdowns.platforms[p][metric] || 0),
        backgroundColor: `rgba(49,130,206,0.2)`,
        borderColor: '#3182ce',
        tension: 0.4,
      }))
    };
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, color: '#3182ce' }}>Analytics Dashboard</Typography>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Dashboard Version</InputLabel>
          <Select value={version} label="Dashboard Version" onChange={e => setVersion(e.target.value)}>
            {dashboardVersions.map(v => <MenuItem key={v.version} value={v.version}>{v.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
  {analytics && typeof analytics === 'object' && analytics.metrics && analytics.breakdowns && analytics.posts && (
        <Paper elevation={4} sx={{ p: 3, borderRadius: 4, background: version === 'v2' ? 'linear-gradient(135deg,#e3f0ff 0%,#f8f8ff 100%)' : undefined }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary" sx={{ mb: 2 }}>
            <Tab label="Overview" />
            <Tab label="Time Ranges" />
            <Tab label="Platforms" />
            <Tab label="Accounts" />
            <Tab label="Media Types" />
            <Tab label="Post Status" />
            <Tab label="Trends" />
            <Tab label="Top Hashtags/Links" />
            <Tab label="Top Posts" />
            <Tab label="Posts" />
            <Tab label="Export" />
          </Tabs>
          {/* Overview */}
          {tab === 0 && analytics.metrics && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Key Metrics</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {Object.entries(analytics.metrics).map(([k, v]) => (
                  <Paper key={k} sx={{ p: 2, textAlign: 'center', minWidth: 120, boxShadow: 2, borderRadius: 2, background: '#f0f4fa' }}>
                    <Typography sx={{ fontWeight: 600 }}>{k}</Typography>
                    <Typography variant="h6" sx={{ color: '#3182ce' }}>{v as any}</Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
          {/* Time Ranges */}
          {tab === 1 && analytics.timeRanges && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Time Range Analytics</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {Object.entries(analytics.timeRanges).map(([range, metrics]) => (
                  <Paper key={range} sx={{ p: 2, minWidth: 200, background: '#f8faff' }}>
                    <Typography sx={{ fontWeight: 600 }}>{range}</Typography>
                    {Object.entries(metrics as any).map(([k, v]) => <Chip key={k} label={`${k}: ${v as any}`} sx={{ m: 0.5 }} />)}
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
          {/* Platforms */}
          {tab === 2 && analytics.breakdowns && analytics.breakdowns.platforms && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Platform Comparison</Typography>
              <Bar data={getPlatformComparisonData(analytics.breakdowns)} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
              {/* Existing Platform Breakdown UI below */}
              <Typography variant="h6" sx={{ mt: 3 }}>Platform Breakdown</Typography>
              <FormControl sx={{ minWidth: 160, mb: 2 }}>
                <InputLabel>Filter Platform</InputLabel>
                <Select value={filterPlatform} label="Filter Platform" onChange={e => setFilterPlatform(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  {Object.keys(analytics.breakdowns.platforms).map(platform => <MenuItem key={platform} value={platform}>{platform}</MenuItem>)}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {Object.entries(analytics.breakdowns.platforms).map(([platform, metrics]) => (
                  (filterPlatform === 'all' || platform === filterPlatform) && (
                    <Paper key={platform} sx={{ p: 2, minWidth: 200, background: '#f8faff' }}>
                      <Typography sx={{ fontWeight: 600 }}>{platform}</Typography>
                      {Object.entries(metrics as any).map(([k, v]) => <Chip key={k} label={`${k}: ${v as any}`} sx={{ m: 0.5 }} />)}
                    </Paper>
                  )
                ))}
              </Box>
            </Box>
          )}
          {/* Accounts/Handles */}
          {tab === 3 && analytics.breakdowns && analytics.breakdowns.handles && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Account/Handle Breakdown</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {Object.entries(analytics.breakdowns.handles).map(([handle, v]) => (
                  <Paper key={handle} sx={{ p: 2, minWidth: 180, background: '#f8faff' }}>
                    <Typography sx={{ fontWeight: 600 }}>{handle}</Typography>
                    <Chip label={`Total Posts: ${(v as any).totalPosts}`} sx={{ m: 0.5 }} />
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
          {/* Media Types */}
          {tab === 4 && analytics.breakdowns && analytics.breakdowns.mediaTypes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Media Type Breakdown</Typography>
              <Pie data={{
                labels: Object.keys(analytics.breakdowns.mediaTypes),
                datasets: [{
                  data: Object.values(analytics.breakdowns.mediaTypes),
                  backgroundColor: ['#3182ce', '#6b46c1', '#63b3ed']
                }]
              }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                {Object.entries(analytics.breakdowns.mediaTypes).map(([k, v]) => <Chip key={k} label={`${k}: ${v as any}`} sx={{ m: 0.5 }} />)}
              </Box>
            </Box>
          )}
          {/* Post Status */}
          {tab === 5 && Array.isArray(analytics.posts) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Post Status Breakdown</Typography>
              <Bar data={{
                labels: Object.keys(analytics.breakdowns.postStatus),
                datasets: [{
                  label: 'Posts',
                  data: Object.values(analytics.breakdowns.postStatus),
                  backgroundColor: '#3182ce'
                }]
              }} />
              <Box sx={{ mt: 4 }}>
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>All Posts</Typography>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ background: '#f0f4fa' }}>
                        <Box component="th" sx={{ p: 1, fontWeight: 600 }}>Caption</Box>
                        <Box component="th" sx={{ p: 1, fontWeight: 600 }}>Date</Box>
                        <Box component="th" sx={{ p: 1, fontWeight: 600 }}>Platforms</Box>
                        <Box component="th" sx={{ p: 1, fontWeight: 600 }}>Status</Box>
                        <Box component="th" sx={{ p: 1, fontWeight: 600 }}>Link</Box>
                        <Box component="th" sx={{ p: 1, fontWeight: 600 }}>Actions</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {analytics.posts.map((post: any) => (
                        <Box component="tr" key={post.id} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                          <Box component="td" sx={{ p: 1 }}>{post.caption}</Box>
                          <Box component="td" sx={{ p: 1 }}>{post.date}</Box>
                          <Box component="td" sx={{ p: 1 }}>{post.platforms?.join(', ')}</Box>
                          <Box component="td" sx={{ p: 1 }}>{post.status}</Box>
                          <Box component="td" sx={{ p: 1 }}>
                            {post.links && post.links.length > 0 ? (
                              post.links.map((link: string) => (
                                <Button key={link} href={link} target="_blank" size="small" variant="outlined" sx={{ mr: 1 }}>View</Button>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">No Link</Typography>
                            )}
                          </Box>
                          <Box component="td" sx={{ p: 1 }}>
                            <Button size="small" variant="contained" color="primary" sx={{ mr: 1 }}>Repost</Button>
                            <Button size="small" variant="outlined" color="secondary" sx={{ mr: 1 }}>Schedule</Button>
                            <Button size="small" variant="outlined" color="error">Delete</Button>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}
          {/* Trends */}
          {tab === 6 && analytics.trends && analytics.trends.engagement && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Trends & Growth</Typography>
              {/* Follower Growth Trends */}
              {followerTrendData && (
                <>
                  <Typography sx={{ mb: 1 }}>Follower Growth</Typography>
                  <Line data={followerTrendData} />
                </>
              )}
              {/* Existing Trends */}
              <Typography sx={{ mt: 3 }}>Engagement</Typography>
              <Line data={{ labels: analytics.trends.engagement.dates, datasets: [{ label: 'Engagement', data: analytics.trends.engagement.values, backgroundColor: 'rgba(106,76,193,0.2)', borderColor: '#6b46c1', tension: 0.4 }] }} />
            </Box>
          )}
          {/* Top Hashtags/Links */}
          {tab === 7 && analytics.top && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Top Hashtags</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {Object.entries(analytics.top.hashtags).map(([tag, count]) => <Chip key={tag} label={`#${tag}: ${count as any}`} sx={{ m: 0.5 }} />)}
              </Box>
              <Typography variant="h5" sx={{ mt: 3 }}>Top Links</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {Object.entries(analytics.top.links).map(([link, count]) => <Chip key={link} label={`${link}: ${count as any}`} sx={{ m: 0.5 }} />)}
              </Box>
            </Box>
          )}
          {/* Top Posts */}
          {tab === 8 && Array.isArray(analytics.posts) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Top Posts (by Engagement)</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {getTopPosts(analytics.posts, 5).map((post: any) => (
                  <Paper key={post.id} sx={{ p: 2, mb: 2, minWidth: 250, background: '#f8faff' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{post.caption}</Typography>
                    <Typography variant="body2">{post.date}</Typography>
                    <Typography variant="body2">Platforms: {post.platforms?.join(', ')}</Typography>
                    <Typography variant="body2">Status: {post.status}</Typography>
                    <Box sx={{ mt: 1 }}>{Object.entries(post.metrics as any).map(([k, v]) => <Chip key={k} label={`${k}: ${v as any}`} sx={{ m: 0.5 }} />)}</Box>
                    <Box sx={{ mt: 1 }}>{post.hashtags?.map((tag: string) => <Chip key={tag} label={`#${tag}`} sx={{ m: 0.5 }} />)}</Box>
                    <Box sx={{ mt: 1 }}>{post.links?.map((link: string) => <Chip key={link} label={link} sx={{ m: 0.5 }} />)}</Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
          {/* Posts */}
          {tab === 9 && Array.isArray(filteredPosts) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Post-wise Analytics</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {filteredPosts.map((post: any) => (
                  <Paper key={post.id} sx={{ p: 2, mb: 2, minWidth: 250, background: '#f8faff' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{post.caption}</Typography>
                    <Typography variant="body2">{post.date}</Typography>
                    <Typography variant="body2">Platforms: {post.platforms?.join(', ')}</Typography>
                    <Typography variant="body2">Status: {post.status}</Typography>
                    <Box sx={{ mt: 1 }}>{Object.entries(post.metrics as any).map(([k, v]) => <Chip key={k} label={`${k}: ${v as any}`} sx={{ m: 0.5 }} />)}</Box>
                    <Box sx={{ mt: 1 }}>{post.hashtags?.map((tag: string) => <Chip key={tag} label={`#${tag}`} sx={{ m: 0.5 }} />)}</Box>
                    <Box sx={{ mt: 1 }}>{post.links?.map((link: string) => <Chip key={link} label={link} sx={{ m: 0.5 }} />)}</Box>
                    {/* Platform-specific status, errors, and links */}
                    {post.platformStatus && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Platform Details:</Typography>
                        {Object.entries(post.platformStatus).map(([platform, info]: any) => (
                          <Box key={platform} sx={{ mb: 1 }}>
                            <Chip label={`Platform: ${platform}`} sx={{ mr: 1 }} />
                            <Chip label={`Status: ${info.status}`} sx={{ mr: 1 }} />
                            {info.error && <Chip label={`Error: ${info.error}`} color="error" sx={{ mr: 1 }} />}
                            {info.link && <Button href={info.link} target="_blank" size="small" variant="outlined" sx={{ ml: 1 }}>View Post</Button>}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
          {/* Export */}
          {tab === 10 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Export & Share</Typography>
              <Button variant="contained" color="primary" onClick={handleExportCSV}>Export All Analytics as CSV</Button>
              <Button variant="outlined" color="secondary" sx={{ ml: 2 }} onClick={handleExportPDF}>Export as PDF</Button>
              <Typography sx={{ mt: 2, color: '#6b46c1' }}>CSV will be copied to clipboard. PDF will be downloaded.</Typography>
            </Box>
          )}
          {/* Engagement Heatmap */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 4, boxShadow: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Engagement Heatmap</Typography>
            <Typography variant="body2" color="text.secondary">Heatmap visualization is temporarily disabled.</Typography>
          </Paper>
        </Paper>
      )}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default OutputDashboard;

